import { useMemo, useState } from "react";
import { useList, useCreate, useUpdate, useDelete, useTranslate } from "@refinedev/core";
import {
  Button,
  Field,
  Modal,
  ConfirmDialog,
  SectionHeader,
  SelectField,
  StatusBadge,
  ToggleField,
  type Column,
  type SelectOption,
} from "@fawkes/ui";

import "./GemeenteManagement.css";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// settings-gemeente: the React port of the Jmix `SettingsDashboardView.buildGemeenteManagementSection()`
// (the "Gemeente" tile detail). Master/detail: a searchable, status-filterable list of municipalities
// on the left; the selected municipality's fields + a woonplaatsen (towns) table with add/edit/delete
// on the right. Woonplaats edits are client-side list mutations followed by a full PUT — the same
// contract `saveGemeente(..., woonplaatsen)` uses in Jmix. CSV import is a deferred follow-up.

const RESOURCE = "gemeenten";

// Parity with `SettingsDashboardView.DUTCH_PROVINCES` — the controlled province vocabulary.
const DUTCH_PROVINCES = [
  "Drenthe",
  "Flevoland",
  "Friesland",
  "Gelderland",
  "Groningen",
  "Limburg",
  "Noord-Brabant",
  "Noord-Holland",
  "Overijssel",
  "Utrecht",
  "Zeeland",
  "Zuid-Holland",
];
const DEFAULT_PROVINCE = "Noord-Brabant";

interface Gemeente {
  id: number;
  naam: string;
  provincie: string;
  actief: boolean;
  woonplaatsen: string[];
}

type StatusFilter = "all" | "active" | "inactive";

// Edit/create form state for a gemeente (name + province).
interface GemeenteForm {
  id: number | null;
  naam: string;
  provincie: string;
}
// Add/edit state for a single woonplaats within the selected gemeente.
interface WoonplaatsForm {
  index: number | null; // null = add
  value: string;
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

export function GemeenteManagement({ onBack }: { onBack: () => void }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useList<Gemeente>({
    resource: RESOURCE,
    pagination: { mode: "off" },
  });
  const { mutate: create } = useCreate();
  const { mutate: update } = useUpdate();
  const { mutate: remove } = useDelete();

  const gemeenten = useMemo(() => data?.data ?? [], [data]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [gemeenteForm, setGemeenteForm] = useState<GemeenteForm | null>(null);
  const [gemeenteFormError, setGemeenteFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Gemeente | null>(null);
  const [woonplaatsForm, setWoonplaatsForm] = useState<WoonplaatsForm | null>(null);
  const [woonplaatsError, setWoonplaatsError] = useState<string | null>(null);

  const selected = gemeenten.find((g) => g.id === selectedId) ?? null;

  const filtered = useMemo(() => {
    const q = normalize(search);
    return gemeenten.filter((g) => {
      if (statusFilter === "active" && !g.actief) return false;
      if (statusFilter === "inactive" && g.actief) return false;
      if (q === "") return true;
      return (
        g.naam.toLowerCase().includes(q) ||
        (g.provincie ?? "").toLowerCase().includes(q) ||
        g.woonplaatsen.some((w) => w.toLowerCase().includes(q))
      );
    });
  }, [gemeenten, search, statusFilter]);

  const statusOptions: SelectOption[] = [
    { value: "all", label: t("instellingen.gemeente.filter.all") },
    { value: "active", label: t("instellingen.gemeente.filter.active") },
    { value: "inactive", label: t("instellingen.gemeente.filter.inactive") },
  ];
  const provinceOptions: SelectOption[] = DUTCH_PROVINCES.map((p) => ({ value: p, label: p }));

  // Case-insensitive duplicate-name guard, parity with the Jmix editor.
  function isDuplicateNaam(naam: string, excludeId: number | null): boolean {
    const n = normalize(naam);
    return gemeenten.some((g) => normalize(g.naam) === n && g.id !== excludeId);
  }

  // Full-payload PUT — the single write path for every gemeente field incl. the woonplaatsen list.
  function saveGemeente(g: Gemeente, onDone?: () => void) {
    update(
      { resource: RESOURCE, id: g.id, values: g },
      { onSuccess: () => { refetch(); onDone?.(); } },
    );
  }

  function submitGemeenteForm() {
    if (!gemeenteForm) return;
    const naam = gemeenteForm.naam.trim();
    if (naam === "") {
      setGemeenteFormError(t("instellingen.gemeente.error.nameRequired"));
      return;
    }
    if (isDuplicateNaam(naam, gemeenteForm.id)) {
      setGemeenteFormError(t("instellingen.gemeente.error.duplicateNaam"));
      return;
    }
    const provincie = gemeenteForm.provincie || DEFAULT_PROVINCE;
    if (gemeenteForm.id == null) {
      create(
        { resource: RESOURCE, values: { naam, provincie, actief: true, woonplaatsen: [] } },
        {
          onSuccess: (res) => {
            refetch();
            const newId = (res?.data as Gemeente | undefined)?.id;
            if (typeof newId === "number") setSelectedId(newId);
            setGemeenteForm(null);
          },
        },
      );
    } else {
      const current = gemeenten.find((g) => g.id === gemeenteForm.id);
      if (!current) return;
      saveGemeente({ ...current, naam, provincie }, () => setGemeenteForm(null));
    }
  }

  function toggleActief(g: Gemeente, actief: boolean) {
    saveGemeente({ ...g, actief });
  }

  function deleteGemeente(g: Gemeente) {
    remove(
      { resource: RESOURCE, id: g.id },
      {
        onSuccess: () => {
          refetch();
          setConfirmDelete(null);
          if (selectedId === g.id) setSelectedId(null);
        },
      },
    );
  }

  // ---- woonplaatsen list edits (always re-PUT the whole list) ----
  function submitWoonplaatsForm() {
    if (!woonplaatsForm || !selected) return;
    const value = woonplaatsForm.value.trim();
    if (value === "") {
      setWoonplaatsError(t("instellingen.gemeente.error.townRequired"));
      return;
    }
    const dup = selected.woonplaatsen.some(
      (w, i) => normalize(w) === normalize(value) && i !== woonplaatsForm.index,
    );
    if (dup) {
      setWoonplaatsError(t("instellingen.gemeente.error.duplicateWoonplaats"));
      return;
    }
    const next = [...selected.woonplaatsen];
    if (woonplaatsForm.index == null) next.push(value);
    else next[woonplaatsForm.index] = value;
    saveGemeente({ ...selected, woonplaatsen: next }, () => setWoonplaatsForm(null));
  }

  function deleteWoonplaats(index: number) {
    if (!selected) return;
    const next = selected.woonplaatsen.filter((_, i) => i !== index);
    saveGemeente({ ...selected, woonplaatsen: next });
  }

  const listColumns: Column<Gemeente>[] = [
    {
      header: t("instellingen.gemeente.col.naam"),
      cell: (g) => (
        <button
          type="button"
          className={`gemeente-row-name${g.id === selectedId ? " gemeente-row-name--active" : ""}`}
          onClick={() => setSelectedId(g.id)}
        >
          {g.naam}
        </button>
      ),
    },
    { header: t("instellingen.gemeente.col.provincie"), cell: (g) => g.provincie },
    {
      header: t("instellingen.gemeente.col.status"),
      cell: (g) => (
        <StatusBadge tone={g.actief ? "success" : "neutral"}>
          {t(g.actief ? "instellingen.gemeente.status.active" : "instellingen.gemeente.status.inactive")}
        </StatusBadge>
      ),
    },
    {
      header: t("instellingen.gemeente.col.woonplaatsen"),
      cell: (g) => g.woonplaatsen.length,
    },
  ];

  const woonplaatsColumns: Column<string>[] = [
    { header: t("instellingen.gemeente.woonplaatsen.col.naam"), cell: (w) => w },
    {
      header: t("instellingen.gemeente.woonplaatsen.col.actions"),
      cell: (w) => {
        const index = selected?.woonplaatsen.indexOf(w) ?? -1;
        return (
          <div className="gemeente-row-actions">
            <Button
              variant="secondary"
              onClick={() => { setWoonplaatsError(null); setWoonplaatsForm({ index, value: w }); }}
            >
              {t("instellingen.gemeente.woonplaatsen.edit")}
            </Button>
            <Button variant="secondary" onClick={() => deleteWoonplaats(index)}>
              {t("instellingen.gemeente.woonplaatsen.delete")}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="gemeente-view-root">
      <div className="gemeente-toolbar">
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
        <SectionHeader>{t("instellingen.gemeente.title")}</SectionHeader>
      </div>

      <div className="gemeente-layout">
        <section className="gemeente-master">
          <div className="gemeente-master-toolbar">
            <Field
              label={t("instellingen.gemeente.searchLabel")}
              placeholder={t("instellingen.gemeente.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <SelectField
              label={t("instellingen.gemeente.filterLabel")}
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            />
            <Button
              variant="primary"
              onClick={() => { setGemeenteFormError(null); setGemeenteForm({ id: null, naam: "", provincie: DEFAULT_PROVINCE }); }}
            >
              {t("instellingen.gemeente.new")}
            </Button>
          </div>

          {isLoading ? (
            <p className="fui-muted">{t("common.loading")}</p>
          ) : isError ? (
            <p className="fui-error">{t("instellingen.gemeente.loadFailed")}</p>
          ) : (
            <IndeelbareTabel tabelKey="settings-gemeente-management-gemeente"
              columns={listColumns}
              rows={filtered}
              getRowKey={(g) => String(g.id)}
              empty={t("instellingen.gemeente.empty")}
            />
          )}
        </section>

        <section className="gemeente-detail">
          {selected == null ? (
            <p className="fui-muted">{t("instellingen.gemeente.detail.empty")}</p>
          ) : (
            <>
              <div className="gemeente-detail-head">
                <div>
                  <h3 className="gemeente-detail-title">{selected.naam}</h3>
                  <p className="gemeente-detail-meta">
                    {selected.provincie}
                    {"  ·  "}
                    <StatusBadge tone={selected.actief ? "success" : "neutral"}>
                      {t(selected.actief ? "instellingen.gemeente.status.active" : "instellingen.gemeente.status.inactive")}
                    </StatusBadge>
                  </p>
                </div>
                <div className="gemeente-row-actions">
                  <Button
                    variant="secondary"
                    onClick={() => { setGemeenteFormError(null); setGemeenteForm({ id: selected.id, naam: selected.naam, provincie: selected.provincie }); }}
                  >
                    {t("instellingen.gemeente.detail.edit")}
                  </Button>
                  <Button variant="secondary" onClick={() => setConfirmDelete(selected)}>
                    {t("instellingen.gemeente.detail.delete")}
                  </Button>
                </div>
              </div>

              <ToggleField
                label={t("instellingen.gemeente.detail.active")}
                checked={selected.actief}
                onChange={(e) => toggleActief(selected, e.target.checked)}
              />

              <div className="gemeente-woonplaatsen">
                <div className="gemeente-woonplaatsen-head">
                  <h4 className="gemeente-woonplaatsen-title">{t("instellingen.gemeente.woonplaatsen.title")}</h4>
                  <Button
                    variant="secondary"
                    onClick={() => { setWoonplaatsError(null); setWoonplaatsForm({ index: null, value: "" }); }}
                  >
                    {t("instellingen.gemeente.woonplaatsen.add")}
                  </Button>
                </div>
                <IndeelbareTabel tabelKey="settings-gemeente-management-woonplaatsen"
                  columns={woonplaatsColumns}
                  rows={selected.woonplaatsen}
                  getRowKey={(w) => w}
                  empty={t("instellingen.gemeente.woonplaatsen.empty")}
                />
              </div>
            </>
          )}
        </section>
      </div>

      {/* Create / edit gemeente modal */}
      <Modal
        open={gemeenteForm != null}
        title={gemeenteForm?.id == null ? t("instellingen.gemeente.create.title") : t("instellingen.gemeente.edit.title")}
        onClose={() => setGemeenteForm(null)}
        footer={
          gemeenteForm && (
            <>
              <Button variant="secondary" onClick={() => setGemeenteForm(null)}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={submitGemeenteForm} disabled={gemeenteForm.naam.trim() === ""}>
                {t("common.save")}
              </Button>
            </>
          )
        }
      >
        {gemeenteForm && (
          <div className="gemeente-form">
            <Field
              label={t("instellingen.gemeente.form.naam")}
              value={gemeenteForm.naam}
              onChange={(e) => { setGemeenteFormError(null); setGemeenteForm({ ...gemeenteForm, naam: e.target.value }); }}
              required
            />
            <SelectField
              label={t("instellingen.gemeente.form.provincie")}
              options={provinceOptions}
              value={gemeenteForm.provincie}
              onChange={(e) => setGemeenteForm({ ...gemeenteForm, provincie: e.target.value })}
            />
            {gemeenteFormError && <p className="fui-error">{gemeenteFormError}</p>}
          </div>
        )}
      </Modal>

      {/* Add / edit woonplaats modal */}
      <Modal
        open={woonplaatsForm != null}
        title={woonplaatsForm?.index == null ? t("instellingen.gemeente.woonplaatsen.add") : t("instellingen.gemeente.woonplaatsen.edit")}
        onClose={() => setWoonplaatsForm(null)}
        footer={
          woonplaatsForm && (
            <>
              <Button variant="secondary" onClick={() => setWoonplaatsForm(null)}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={submitWoonplaatsForm} disabled={woonplaatsForm.value.trim() === ""}>
                {t("common.save")}
              </Button>
            </>
          )
        }
      >
        {woonplaatsForm && (
          <div className="gemeente-form">
            <Field
              label={t("instellingen.gemeente.woonplaatsen.label")}
              value={woonplaatsForm.value}
              onChange={(e) => { setWoonplaatsError(null); setWoonplaatsForm({ ...woonplaatsForm, value: e.target.value }); }}
              required
            />
            {woonplaatsError && <p className="fui-error">{woonplaatsError}</p>}
          </div>
        )}
      </Modal>

      {/* Delete gemeente confirm modal */}
      {confirmDelete && (
        <ConfirmDialog
          title={t("instellingen.gemeente.detail.delete")}
          confirmLabel={t("instellingen.gemeente.detail.delete")}
          cancelLabel={t("common.cancel")}
          danger
          onConfirm={() => deleteGemeente(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        >
          {t("instellingen.gemeente.delete.confirm", { args: [confirmDelete.naam] })}
        </ConfirmDialog>
      )}
    </div>
  );
}
